import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useImpersonation } from "../contexts/ImpersonationContext";
import { useEndImpersonation } from "../hooks/usePlatformAdmin";

function formatRemaining(ms: number): string {
  if (ms <= 0) return "expired";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export function ImpersonationBanner() {
  const { active, end } = useImpersonation();
  const endMutation = useEndImpersonation();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  const expiresAt = active ? new Date(active.expires_at).getTime() : 0;
  const remaining = expiresAt - Date.now();

  // Auto-end when the 60-minute window runs out: the impersonation token
  // expires with the session, so every request would 401 from here on —
  // restore the admin identity instead of leaving a dead session up.
  useEffect(() => {
    if (active && remaining <= 0) {
      end();
    }
  }, [active, remaining, end]);

  if (!active) return null;

  const handleEnd = async () => {
    try {
      await endMutation.mutateAsync({ impersonation_id: active.id });
    } catch {
      // End locally even if the server call fails — frontend drops the imp token.
    }
    end();
  };

  return (
    <div className="sticky top-0 z-50 border-b-2 border-rose-800 bg-rose-600 px-4 py-2.5 text-white shadow-lg">
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-5 w-5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold uppercase tracking-wide">
            Impersonating {active.target_email}
          </p>
          <p className="truncate text-xs opacity-90">
            Read-only session · {formatRemaining(remaining)} left
            {active.reason && <> · {active.reason}</>}
          </p>
        </div>
        <span className="hidden rounded-md bg-rose-800/70 px-2.5 py-1 font-mono text-sm font-semibold tabular-nums sm:inline-block">
          {formatRemaining(remaining)}
        </span>
        <Link
          to="/platform/audit"
          className="hidden rounded-md bg-white/10 px-2.5 py-1.5 text-xs font-medium hover:bg-white/20 md:inline-block"
        >
          View audit
        </Link>
        <button
          onClick={handleEnd}
          disabled={endMutation.isPending}
          className="rounded-md bg-white px-3.5 py-1.5 text-sm font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
        >
          End impersonation
        </button>
      </div>
    </div>
  );
}
